import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { PDFDocument } from "pdf-lib";
import axios from "axios";

import MakeAPICall from "../../apiCalls/common";
import {
  readS3FileBufferApiRoute,
  mergeTicketDocumentsApiRoute,
  attachTicketDocumentToConsultNoteApiRoute
} from "../../apiRoutes";
import { store } from "../../reduxStore/store";

import Button from "../../components/button";
import Loader from "../../components/loader";
import CustomModal from "../../components/modal";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const THUMBNAIL_WIDTH = 140;

// the S3 file buffer endpoint takes just the folder+filename, not the full https://... url
const getS3Key = url => url.replace(/^https?:\/\/[^/]+\//, "");

const base64ToUint8Array = base64 => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

// fetch the raw PDF bytes for an attachment through the backend S3 proxy (S3 has no CORS policy)
const fetchAttachmentBytes = async url => {
  const accessToken = store.getState().auth.user && store.getState().auth.user.accessToken;

  const res = await axios({
    method: "GET",
    url: readS3FileBufferApiRoute,
    params: { s3FolderWithFile: getS3Key(url) },
    headers: { Authorization: "Bearer " + (accessToken || "") }
  });

  return base64ToUint8Array(res.data.data);
};

// renders every page of one attachment PDF as a clickable thumbnail
const AttachmentPages = ({ attachment, index, selectedPages, onTogglePage }) => {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const renderPages = async () => {
      setIsLoading(true);
      setLoadError("");

      try {
        const bytes = await fetchAttachmentBytes(attachment.url);
        const loadingTask = pdfjsLib.getDocument({ data: bytes });
        const pdf = await loadingTask.promise;
        const renderedPages = [];

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1 });
          const scale = THUMBNAIL_WIDTH / viewport.width;
          const scaledViewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;

          await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport: scaledViewport
          }).promise;

          renderedPages.push({ pageNumber, dataUrl: canvas.toDataURL() });
        }

        if (!cancelled) {
          setPages(renderedPages);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError("Could not load this attachment. Please try again.");
          setIsLoading(false);
        }
      }
    };

    renderPages();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment.url]);

  return (
    <div className="mb-4">
      <p className="mb-2">
        <strong>{attachment.documentType || "Document"}</strong>
      </p>
      {isLoading && <p className="text-muted">Loading pages...</p>}
      {loadError && <p className="error">{loadError}</p>}
      {!isLoading && !loadError && (
        <div className="d-flex flex-row flex-wrap">
          {pages.map(page => {
            const pageKey = `${index}-${page.pageNumber}`;
            const selectionOrder = selectedPages.findIndex(p => p.key === pageKey) + 1;
            const isSelected = selectionOrder > 0;

            return (
              <div
                key={pageKey}
                onClick={() => onTogglePage({ key: pageKey, attachmentIndex: index, url: attachment.url, pageNumber: page.pageNumber })}
                className="position-relative mr-2 mb-2"
                style={{
                  cursor: "pointer",
                  border: isSelected ? "3px solid #f5ab35" : "1px solid #e0e0e0"
                }}
              >
                <img src={page.dataUrl} alt={`Page ${page.pageNumber}`} style={{ display: "block" }} />
                <div className="text-center" style={{ fontSize: "12px" }}>
                  Page {page.pageNumber}
                </div>
                {isSelected && (
                  <span
                    className="position-absolute"
                    style={{
                      top: "4px",
                      left: "4px",
                      backgroundColor: "#f5ab35",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "22px",
                      height: "22px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px"
                    }}
                  >
                    {selectionOrder}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ATTACHMENT_TYPE_OPTIONS = [
  { value: "PathologyResults", label: "Pathology Results" },
  { value: "RadiologyResults", label: "Radiology Results" },
  { value: "SpecialistLetter", label: "Specialist Letter" }
];

// lets the admin pick pages across multiple attachments, merges them into one new PDF client-side,
// and uploads the merged PDF to be attached to the ticket's consult note
const AttachmentsPdfMerger = ({ attachments, ticketId, mergedAttachment, onMergeSuccess }) => {
  const [showAttachments, setShowAttachments] = useState(false);
  const [selectedPages, setSelectedPages] = useState([]);
  const [isMerging, setIsMerging] = useState(false);
  const [mergeError, setMergeError] = useState("");
  const [mergeSuccess, setMergeSuccess] = useState(false);
  const [mergedAttachmentUrl, setMergedAttachmentUrl] = useState(mergedAttachment || "");
  const [apiCallParams, setApiCallParams] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState("");
  const [attachmentType, setAttachmentType] = useState("");
  const [notesError, setNotesError] = useState("");
  const [isAttachingNote, setIsAttachingNote] = useState(false);
  const [attachNoteSuccess, setAttachNoteSuccess] = useState(false);

  useEffect(() => {
    setMergedAttachmentUrl(mergedAttachment || "");
  }, [mergedAttachment]);

  const submitNotes = () => {
    if (!attachmentType) {
      setNotesError("Please select a result type");
      return;
    }
    setNotesError("");
    setIsAttachingNote(true);

    setApiCallParams({
      reqType: "POST",
      reqUrl: attachTicketDocumentToConsultNoteApiRoute,
      dataObj: {
        ticketId,
        note: notes,
        attachmentType
      },
      successCallback: () => {
        setIsAttachingNote(false);
        setAttachNoteSuccess(true);
        setShowNotesModal(false);
        setNotes("");
        setAttachmentType("");
      },
      failureCallback: () => {
        setIsAttachingNote(false);
        setNotesError("Could not attach the note. Please try again.");
      }
    });
  };

  const togglePage = page => {
    setSelectedPages(prev => {
      const exists = prev.find(p => p.key === page.key);
      if (exists) {
        return prev.filter(p => p.key !== page.key);
      }
      return [...prev, page];
    });
  };

  const uploadMergedPdf = mergedBytes => {
    const formData = new FormData();
    const blob = new Blob([mergedBytes], { type: "application/pdf" });
    formData.append("files", blob, "merged-result.pdf");
    formData.append("ticketId", ticketId);

    setApiCallParams({
      reqType: "POST",
      reqUrl: mergeTicketDocumentsApiRoute,
      dataObj: formData,
      successCallback: res => {
        setIsMerging(false);
        setMergeSuccess(true);
        setMergedAttachmentUrl(res.data.data.mergedAttachment || "");
        setSelectedPages([]);
        if (onMergeSuccess) {
          onMergeSuccess(res.data.data);
        }
      },
      failureCallback: () => {
        setIsMerging(false);
        setMergeError("Could not upload the merged PDF. Please try again.");
      }
    });
  };

  const generateMergedPdf = async () => {
    if (selectedPages.length === 0) {
      return;
    }

    setIsMerging(true);
    setMergeError("");
    setMergeSuccess(false);

    try {
      const mergedPdf = await PDFDocument.create();

      // cache loaded source PDFs by url so we don't refetch per page
      const sourcePdfCache = {};

      for (const selectedPage of selectedPages) {
        if (!sourcePdfCache[selectedPage.url]) {
          const bytes = await fetchAttachmentBytes(selectedPage.url);
          sourcePdfCache[selectedPage.url] = await PDFDocument.load(bytes);
        }

        const sourcePdf = sourcePdfCache[selectedPage.url];
        const [copiedPage] = await mergedPdf.copyPages(sourcePdf, [selectedPage.pageNumber - 1]);
        mergedPdf.addPage(copiedPage);
      }

      const mergedBytes = await mergedPdf.save();
      uploadMergedPdf(mergedBytes);
    } catch (err) {
      setMergeError("Could not generate the merged PDF. Please try again.");
      setIsMerging(false);
    }
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="form-group row">
      <label className="col-sm-2 col-form-label">Attachments:</label>
      <div className="col-sm-10 position-relative">
        {isMerging && <Loader />}

        <div className="d-flex flex-row flex-wrap mb-2">
          {attachments.map((attachment, index) => (
            <a
              key={attachment._id || index}
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="received-results-doc-link mr-3 mb-1"
            >
              {attachment.documentType || "Document"}
            </a>
          ))}
        </div>

        <div className="d-flex flex-row align-items-center mb-3">
          <Button
            clickEvent={() => setShowAttachments(true)}
            disabled={showAttachments}
            title="View Attachment Pages"
          />
          {mergedAttachmentUrl && (
            <>
              <Button
                className="ml-2 green"
                clickEvent={() => window.open(mergedAttachmentUrl, "_blank", "noopener,noreferrer")}
                title="View merged PDF"
              />
              <Button
                className="ml-2 blue"
                clickEvent={() => setShowNotesModal(true)}
                title="Add Notes"
              />
            </>
          )}
        </div>

        {showAttachments && (
          <>
            <p className="text-muted">Click pages to select them in the order you want them merged.</p>
            {attachments.map((attachment, index) => (
              <AttachmentPages
                key={attachment._id || index}
                attachment={attachment}
                index={index}
                selectedPages={selectedPages}
                onTogglePage={togglePage}
              />
            ))}
            {mergeError && <p className="error">{mergeError}</p>}
            {mergeSuccess && <p className="text-success">Merged PDF uploaded successfully.</p>}
            <Button
              clickEvent={generateMergedPdf}
              disabled={selectedPages.length === 0 || isMerging}
              title={`Generate PDF (${selectedPages.length} page${selectedPages.length === 1 ? "" : "s"} selected)`}
            />
          </>
        )}
        {attachNoteSuccess && <p className="text-success">Note attached to consult note successfully.</p>}
        <CustomModal
          title="Add Notes"
          okayText="Submit"
          cancelText="Cancel"
          openModal={showNotesModal}
          cancelEvent={() => {
            setShowNotesModal(false);
            setNotes("");
            setAttachmentType("");
            setNotesError("");
          }}
          okayEvent={submitNotes}
          modalBodyContent={
            <div style={{ position: "relative" }}>
              {isAttachingNote && <Loader />}
              <label htmlFor="mergedPdfAttachmentType">Result Type</label>
              <select
                id="mergedPdfAttachmentType"
                className="form-control mb-3"
                value={attachmentType}
                onChange={e => {
                  setAttachmentType(e.target.value);
                  setNotesError("");
                }}
              >
                <option value="">Select result type</option>
                {ATTACHMENT_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label htmlFor="mergedPdfNotes">Notes</label>
              <textarea
                id="mergedPdfNotes"
                className="form-control"
                rows="4"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add a note for this merged PDF"
              />
              {notesError && <p className="error mt-2">{notesError}</p>}
            </div>
          }
        />
        {/* API Call */}
        <MakeAPICall apiParam={apiCallParams} />
      </div>
    </div>
  );
};

export default AttachmentsPdfMerger;

AttachmentsPdfMerger.propTypes = {
  attachments: PropTypes.array,
  ticketId: PropTypes.string,
  mergedAttachment: PropTypes.string,
  onMergeSuccess: PropTypes.func
};
